import {
  channelRoute,
  channelRouteOf,
  feedScrape,
  formatRoute,
  listingFacts,
  pickImage,
  retargetVideoFrameUrl,
  richTextToPlain,
  type ChannelSummary,
  type FeedItem,
  type FeedScrape,
  type PlaylistSummary,
  type VideoSummary,
  type WatchProgressMark,
} from '@visious/core';
import { formatDuration } from '../format';
import { fmt, text } from '../strings';

/**
 * One video, ready to render in a list — the view model five screens share.
 *
 * This was built inside `screens/watch/model.ts` and said it would move "when the second caller
 * appears, not before". 2b is that caller, and 2c's results, 2d's channel tabs and 2e's playlist
 * entries are the next three. What moved with it is every decision that is *not* the layout: which
 * image to fetch, what the corner of the thumbnail says, where the byline points, and — new in 2b —
 * which of the three metadata fields the row is entitled to state at all.
 *
 * It is not in `ui/` because `ui/` is domain-free by design: `Thumbnail` takes a URL and a badge and
 * knows nothing about videos. This knows about videos and nothing about CSS.
 */

/**
 * A small mark on a row, for something the *page* knows that the video record does not — 3b's
 * "the instance has this flagged as unseen", and 4b's "you have watched this".
 *
 * On `ListingItem` rather than passed alongside it because the card draws it, and a second prop
 * threaded through `ListingGrid` for one screen is how a shared grid grows a screen-shaped hole.
 *
 * Since 4b `toListingItems` produces one itself — {@link progressMarker} — and a caller that knows
 * something stronger still replaces it. The precedence is deliberate rather than incidental: the
 * subscriptions feed's "Unseen" is *the instance's* claim about a row and there is one slot, so it wins
 * over ours, and the reader still sees the progress bar underneath.
 */
export interface ListingMarker {
  readonly label: string;
  /** The whole explanation, since the label has room for a word. */
  readonly title: string;
}

/**
 * How far into this video the reader got — 4b's bar along the bottom edge of a thumbnail.
 *
 * **Separate from {@link ListingMarker} because the two reach different readers**, and that is the whole
 * reason both exist. The bar lives inside the thumbnail, and a card's thumbnail link is `aria-hidden`
 * with `tabIndex={-1}` (`ListingCard`) so that a keyboard and a screen reader see exactly one link per
 * card — which means *anything* drawn there is decoration by construction. So the bar is the visual
 * statement and the marker is the spoken one; drawing the bar alone would make progress invisible to
 * assistive technology, and a page full of "Resume" chips with no bar would be noise.
 */
export interface ListingProgress {
  /** 0–1. `1` for a video watched to the end, including one whose length was never reported. */
  readonly fraction: number;
  readonly complete: boolean;
  /** The sentence for the bar's `title`, since it has no words of its own. */
  readonly title: string;
}

/**
 * What the app knows about the reader having watched a video — the two sources, together, because
 * every question a row asks needs both and asking them separately is how they drift.
 *
 * `mark` is the **local** playhead (4b's store, this browser only, absent entirely while the reader
 * has resume positions switched off). `inHistory` is the **account's** claim, which is the only
 * source that crosses devices and the only one left when nothing is stored locally.
 */
export interface WatchedFacts {
  readonly mark: WatchProgressMark | null;
  readonly inHistory: boolean;
}

/**
 * How a row learns whether it has been watched: one lookup, threaded through every listing model
 * beside `nowEpochSec`.
 *
 * The type lives here rather than with the hook that produces it (`useWatchProgressLookup`) so that a
 * model file never has to import from a React one — the same direction R13 keeps everything else
 * pointing. `NO_PROGRESS` is what a list with nothing to draw passes, explicitly.
 */
export type ProgressLookup = (videoId: string) => WatchedFacts;

/** One value rather than a fresh object per row, so a list drawing none re-renders nothing. */
const NOTHING_WATCHED: WatchedFacts = { mark: null, inHistory: false };

export const NO_PROGRESS: ProgressLookup = () => NOTHING_WATCHED;

/**
 * The "watched" line, in one place: **the stored position decides where there is one, and the
 * account's history decides where there is not.**
 *
 * A position that is finished or at least 95 % in is watched — the same threshold the store's own
 * `complete` fires at (95 % / 15-from-the-end), which also survives a video whose length was never
 * reported. A position short of that is *not*, history or no history: the reader started this and
 * stopped, which is what "Resume" says and what the bar under the thumbnail shows. Only with nothing
 * stored at all does the history's bare id decide, and it is then the whole of what is known.
 *
 * Three readers share this and must not drift: every grid's watched fade
 * ({@link ListingItem.watched}), the "Watched" mark on a thumbnail, and the subscriptions feed's
 * Hide-watched filter.
 */
const SEEN_FRACTION = 0.95;

export function isWatchedVideo(facts: WatchedFacts): boolean {
  return facts.mark === null ? facts.inHistory : isSeenMark(facts.mark);
}

function isSeenMark(mark: WatchProgressMark): boolean {
  return mark.complete || (mark.fraction !== null && mark.fraction >= SEEN_FRACTION);
}

/**
 * The grid search, as one line every grid agrees on: a case-insensitive substring test over the
 * words a card shows (title, and the author where the caller has one). Display-only by design — it
 * narrows what is on the page and never what is fetched — so an empty or whitespace query matches
 * everything, which is what lets callers apply it unconditionally.
 */
export function matchesGridSearch(q: string, ...texts: readonly (string | null)[]): boolean {
  const needle = q.trim().toLowerCase();
  if (needle === '') return true;
  return texts.some((value) => value !== null && value.toLowerCase().includes(needle));
}

/** The corner of a thumbnail. `null` when the record does not know its own length (see `listingFacts`). */
export interface ListingBadge {
  readonly kind: 'duration' | 'live' | 'upcoming';
  readonly text: string;
  /** A `title` for the ones whose two words hide something — a premiere date. */
  readonly title: string | null;
}

export interface ListingItem {
  readonly videoId: string;
  /** The in-app watch route, already built (R13 — `.tsx` builds no URLs). */
  readonly href: string;
  readonly title: string;
  readonly author: string;
  readonly authorHref: string | null;
  readonly authorVerified: boolean;
  readonly thumbnailUrl: string | null;
  readonly badge: ListingBadge | null;
  /**
   * The raw seconds behind a duration badge, `null` otherwise — the hover preview's clamp
   * (2026-08-02): the standalone storyboard track over-emits blank trailing tiles and carries no
   * `count × interval` to cut them by, so the record's own length is the only knife.
   */
  readonly durationSec: number | null;
  readonly viewCountText: string | null;
  readonly viewCountTitle: string | null;
  readonly publishedText: string | null;
  /** Only where the endpoint's dates are genuine — see {@link ListingOptions.absoluteDates}. */
  readonly publishedTitle: string | null;
  /** 4b's watched/resume word, or a screen's own — see {@link ListingMarker} for the precedence. */
  readonly marker: ListingMarker | null;
  /** 4b's bar. `null` when nothing is stored for this video, or the reader turned it off. */
  readonly progress: ListingProgress | null;
  /**
   * The watched fade, and the "Watched" mark on the thumbnail's own corner ({@link isWatchedVideo}).
   * The History grid draws neither: every row there is watched by construction.
   */
  readonly watched: boolean;
}

export interface ListingOptions {
  readonly nowEpochSec: number;
  /**
   * The narrowest source that still covers the box at 2×. A rail row is 168 px and a grid card is
   * ~360 px, and `pickImage` is smallest-sufficient because the difference is 13.5 KB against 266 KB
   * per row. See `CARD_THUMBNAIL_MIN_WIDTH`: only three of the nine declared sizes are 16:9, so the
   * choice is 320×180 or 1280×720 and nothing between.
   */
  readonly thumbnailMinWidth: number;
  /**
   * Whether this endpoint's `published` may carry an absolute-date tooltip.
   *
   * `/api/v1/popular`'s dates are genuine (40 rows, 39 distinct times of day). Search's and
   * `recommendedVideos[]`' are **back-computed from the relative text** — every row carries the
   * request's own time of day and "2 years ago" resolves to exactly two years before the request —
   * so they are accurate to the unit that was displayed and no further. "2 years ago" is then the
   * honest rendering and "27 July 2024" is not, which is why this is per caller rather than a
   * property of the row.
   */
  readonly absoluteDates: boolean;
  /**
   * What the row's own response carried. Omit it for a single record with no list context, and pass
   * it for a feed — a response where *no* row has a duration, a count or a date is a scrape that
   * produced titles only, and its rows must not state the zeros it filled them with.
   */
  readonly scrape?: FeedScrape | null;
  /**
   * 4b's stored playheads and the account's history, as one lookup (`useWatchProgressLookup`).
   *
   * **Required rather than optional**, unlike `scrape`, and for the opposite reason. A missing `scrape`
   * can be computed here, so a caller that forgets it still gets the right answer; a missing lookup
   * cannot be — it is platform storage and an account read — so an optional one would give any screen
   * that forgot it a silently featureless page. `NO_PROGRESS` is the explicit way to say "this list
   * draws none".
   */
  readonly progress: ProgressLookup;
}

export function toListingItems(
  videos: readonly VideoSummary[],
  options: ListingOptions,
): readonly ListingItem[] {
  // Computed here when the caller did not bring one, so no call site can forget it and get a page
  // full of "0:00 · 0 views · now" — the failure this exists to prevent.
  const scrape =
    options.scrape === undefined ? feedScrape(videos, options.nowEpochSec) : options.scrape;

  return videos.map((video): ListingItem => {
    const facts = listingFacts(video, options.nowEpochSec, scrape);
    const route = channelRouteOf(video);
    const watched = options.progress(video.videoId);

    return {
      videoId: video.videoId,
      href: formatRoute({
        kind: 'watch',
        videoId: video.videoId,
        // A listing click is a fresh video: it inherits neither a timestamp nor, until the queue
        // exists (2e/3c), a playlist.
        startSec: null,
        playlistId: null,
        index: null,
      }),
      title: video.title,
      author: video.author,
      // `channelRouteOf` rather than "prefer authorId, else authorUrl": a collaboration video has
      // `authorId: ""` and `authorUrl: "/channel/"`, and the naive test renders a byline that
      // navigates to "Nothing here" (`links/routes.ts`).
      authorHref: route === null ? null : formatRoute(route),
      authorVerified: video.authorVerified,
      thumbnailUrl:
        pickImage(video.thumbnails, { minWidth: options.thumbnailMinWidth, widescreen: true })?.url ??
        null,
      badge: toBadge(facts.badge),
      durationSec: facts.badge.kind === 'duration' ? facts.badge.seconds : null,
      viewCountText: fmt().count(facts.viewCount),
      // Only where the exact figure says something the compact one hid — see `distinctExactTitle`.
      viewCountTitle: fmt().distinctExactTitle(
        fmt().count(facts.viewCount),
        fmt().exactCount(facts.viewCount),
      ),
      publishedText: fmt().published(facts.publishedEpochSec, options.nowEpochSec),
      publishedTitle: options.absoluteDates ? fmt().absoluteDate(facts.publishedEpochSec) : null,
      marker: progressMarker(watched),
      progress: toListingProgress(watched.mark),
      watched: isWatchedVideo(watched),
    };
  });
}

/* ------------------------------------------------------------------------------------------------
 * 4b — one stored playhead, rendered two ways
 * ---------------------------------------------------------------------------------------------- */

/**
 * The bar. `null` where there is nothing to draw — no stored position, or one on a video whose length
 * was never reported and which is not finished either (a live stream), where a fraction would be a
 * claim about a whole that nothing measured.
 */
export function toListingProgress(mark: WatchProgressMark | null): ListingProgress | null {
  if (mark === null || mark.fraction === null) return null;
  return {
    fraction: mark.fraction,
    complete: mark.complete,
    title: progressSentence(mark),
  };
}

/**
 * The word. `Watched` once it is finished, `Resume` while it is not — and `Resume` rather than a second
 * "Watched" because it is the *actionable* reading: clicking the row continues from the stored position,
 * which is a different promise from "you have seen this" and the one the reader is about to act on.
 *
 * A mark with no drawable fraction still gets a word, which is why this is not folded into
 * {@link toListingProgress}: a finished live stream is genuinely watched and has no bar.
 *
 * **A video known only from the account's history gets the word too**, and that is the whole of what
 * assistive technology hears about it: the "Watched" mark drawn on the thumbnail rides inside a frame
 * that is `aria-hidden` by construction, so a row with no marker would be silent about a state the
 * screen is stating.
 */
export function progressMarker(facts: WatchedFacts): ListingMarker | null {
  const words = text().listing.progress;
  const { mark } = facts;
  if (mark === null) {
    return facts.inHistory ? { label: words.watched, title: words.inHistory } : null;
  }
  return mark.complete
    ? { label: words.watched, title: progressSentence(mark) }
    : { label: words.resume, title: progressSentence(mark) };
}

function progressSentence(mark: WatchProgressMark): string {
  const words = text().listing.progress;
  if (mark.complete) return words.complete;
  const position = formatDuration(mark.positionSec);
  return mark.durationSec === null
    ? words.stoppedAt(position)
    : words.stoppedAtOf(position, formatDuration(mark.durationSec));
}

/* ------------------------------------------------------------------------------------------------
 * The other two kinds of row
 * ---------------------------------------------------------------------------------------------- */

/**
 * A channel, ready to render in a results grid.
 *
 * **`videoCount` is deliberately absent, and that is a measurement rather than an omission.** A search
 * channel record carries the field and it read `0` for **20 of 20** rows on a live page — the same
 * shape 2b's trending finding has, one field wide: a *row* reporting zero is ambiguous, a *response*
 * where every row reports zero is not a count. `subCount` is genuine over the same 20 rows, so that
 * one is shown.
 */
export interface ChannelRow {
  readonly channelId: string;
  readonly href: string;
  readonly name: string;
  /** `@handle`, without the `@` stripped — it is a label here, not a route. Null for "- Topic" channels. */
  readonly handle: string | null;
  readonly verified: boolean;
  readonly autoGenerated: boolean;
  readonly avatarUrl: string | null;
  readonly subscriberText: string | null;
  readonly subscriberTitle: string | null;
  /** Plain text from the node tree, so no HTML reaches a renderer (R8) and no `<b>` highlight either. */
  readonly description: string;
}

export interface PlaylistRow {
  readonly playlistId: string;
  readonly href: string;
  readonly title: string;
  readonly author: string;
  readonly authorHref: string | null;
  readonly authorVerified: boolean;
  readonly thumbnailUrl: string | null;
  /** Genuine here, unlike a channel row's: measured 17, 10, 38, 20, 25, 80, 178, 279… */
  readonly videoCountText: string | null;
}

/**
 * One row of a heterogeneous list — `/api/v1/search`, and 2d's channel search after it.
 *
 * The three kinds share one grid rather than being split into sections, because **the order is the
 * answer**: a relevance ranking that puts a channel third is saying something, and hoisting every
 * channel into a band above the videos would silently re-sort the one thing the endpoint got right.
 * The `?sort_by=top` lesson in a new place — do not re-sort what you did not rank.
 */
export type ListingRow =
  | { readonly kind: 'video'; readonly key: string; readonly video: ListingItem }
  | { readonly kind: 'channel'; readonly key: string; readonly channel: ChannelRow }
  | { readonly kind: 'playlist'; readonly key: string; readonly playlist: PlaylistRow };

/**
 * The narrowest avatar that still covers the box at 2×. `authorThumbnails[]` is sorted **ascending**
 * (the opposite of `videoThumbnails[]`), which is exactly the asymmetry `pickImage` exists to hide.
 */
const AVATAR_MIN_WIDTH = 176;

/** What a heterogeneous list produced, with the rows it could not render *counted* rather than lost. */
export interface ListingRows {
  readonly rows: readonly ListingRow[];
  /**
   * `kind: 'unknown'` items — a `type` this build does not recognise. Kept as raw type names because
   * that is the reportable fact (`parse/feed.ts`: an unknown type is a *skippable* row, deliberately
   * not the same outcome as a dropped one). Counted per distinct name.
   */
  readonly unknownTypes: readonly { readonly rawType: string; readonly count: number }[];
}

export function toListingRows(items: readonly FeedItem[], options: ListingOptions): ListingRows {
  // Computed across the *videos in this response*, not across all rows: a page of 20 channels has no
  // durations by construction, and feeding it to `feedScrape` would grade a healthy page degenerate.
  const videos = items.flatMap((item) => (item.kind === 'video' ? [item.video] : []));
  const scrape =
    options.scrape === undefined ? feedScrape(videos, options.nowEpochSec) : options.scrape;
  const withScrape: ListingOptions = { ...options, scrape };

  const rows: ListingRow[] = [];
  const unknown = new Map<string, number>();

  for (const item of items) {
    switch (item.kind) {
      case 'video': {
        const [video] = toListingItems([item.video], withScrape);
        if (video !== undefined) rows.push({ kind: 'video', key: `video:${video.videoId}`, video });
        break;
      }
      case 'channel': {
        const channel = toChannelRow(item.channel);
        rows.push({ kind: 'channel', key: `channel:${channel.channelId}`, channel });
        break;
      }
      case 'playlist': {
        const playlist = toPlaylistRow(item.playlist);
        rows.push({ kind: 'playlist', key: `playlist:${playlist.playlistId}`, playlist });
        break;
      }
      case 'unknown':
        unknown.set(item.rawType, (unknown.get(item.rawType) ?? 0) + 1);
        break;
    }
  }

  return {
    rows,
    unknownTypes: [...unknown].map(([rawType, count]) => ({ rawType, count })),
  };
}

function toChannelRow(channel: ChannelSummary): ChannelRow {
  const subscribers = channel.subscriberCount;

  return {
    channelId: channel.channelId,
    href: formatRoute(channelRoute(channel.channelId)),
    name: channel.name,
    handle: channel.handle,
    verified: channel.verified,
    autoGenerated: channel.autoGenerated,
    avatarUrl: pickImage(channel.thumbnails, { minWidth: AVATAR_MIN_WIDTH })?.url ?? null,
    subscriberText: subscribers === null ? null : fmt().count(subscribers),
    subscriberTitle:
      subscribers === null
        ? null
        : fmt().distinctExactTitle(fmt().count(subscribers), fmt().exactCount(subscribers)),
    // From the node tree rather than the raw string: a search channel's `descriptionHtml` carries
    // `<b>` query highlighting the plain field does not have, and R8's rule is that no HTML leaves a
    // parser. `richTextToPlain` gives back the text with the mentions and links intact as text.
    description: richTextToPlain(channel.descriptionNodes).trim() || channel.description.trim(),
  };
}

function toPlaylistRow(playlist: PlaylistSummary): PlaylistRow {
  const route = channelRouteOf(playlist);
  const count = playlist.videoCount;

  return {
    playlistId: playlist.playlistId,
    href: formatRoute({ kind: 'playlist', playlistId: playlist.playlistId }),
    title: playlist.title,
    author: playlist.author,
    authorHref: route === null ? null : formatRoute(route),
    authorVerified: playlist.authorVerified,
    /*
     * A single `ImageRef`, not a list: `playlistThumbnail` is one bare `i.ytimg.com` URL, already
     * rewritten to the instance by the parser (R2). So `pickImage` has nothing to choose between, and
     * the one URL it hands over is `hq720.jpg` — **275 KB** for a box drawn at 278 px.
     * `retargetVideoFrameUrl` is `pickImage`'s answer for a single-URL endpoint, and it leaves the
     * `/s_p/…` mix-playlist form alone (see it for the measurements and for why that matters).
     */
    thumbnailUrl:
      playlist.thumbnail === null
        ? null
        : retargetVideoFrameUrl(playlist.thumbnail.url, 'mqdefault'),
    videoCountText: count === null || count <= 0 ? null : fmt().count(count),
  };
}

function toBadge(badge: ReturnType<typeof listingFacts>['badge']): ListingBadge | null {
  switch (badge.kind) {
    case 'duration':
      return { kind: 'duration', text: formatDuration(badge.seconds), title: null };
    case 'live':
      return { kind: 'live', text: text().listing.badge.live, title: null };
    case 'upcoming':
      return {
        kind: 'upcoming',
        text: text().listing.badge.upcoming,
        title: fmt().absoluteDate(badge.premiereAtEpochSec),
      };
    case 'unknown':
      // Deliberately no badge, and this is the arm that keeps being right: a live recommendation
      // reports `lengthSeconds: 0`, 11 of 40 `/popular` rows carry no duration at all (one live, one
      // 87 s, one 9 476 s when probed), and every default-trending row reports zero. A duration
      // formatter answers all of them with a confident "0:00".
      return null;
  }
}
